/**
 * XLSX Export Module
 *
 * One workbook per processing result, with consolidated sheets (account is a
 * COLUMN, not a separate sheet):
 *   - Summary    one row per account (P&L, commission, cash flow, balances)
 *   - Trades     one row per execution (deduplicated), commission per side
 *   - Positions  FIFO closed positions with per-position realized P&L
 *   - Economics  per-contract $/point, tick value, fee/contract
 *   - Journal    cash movements (deposits / withdrawals / transfers / data fees)
 */

import * as XLSX from 'xlsx';
import type { ProcessingResult } from './types';

const money = (v: number | null) => (v == null ? '' : v);
const round = (v: number) => Math.round(v * 100) / 100;

export function downloadFullResultXLSX(result: ProcessingResult, filename: string): void {
  const wb = XLSX.utils.book_new();
  addSummarySheet(wb, result);
  addTradesSheet(wb, result);
  addPositionsSheet(wb, result);
  addOpenPositionsSheet(wb, result);
  addEconomicsSheet(wb, result);
  addJournalSheet(wb, result);
  XLSX.writeFile(wb, `${filename}_statement.xlsx`);
}

function addSummarySheet(wb: XLSX.WorkBook, r: ProcessingResult): void {
  const rows = r.accounts.map(a => {
    const s = a.summary;
    const fifoPnL = a.positions.reduce((sum, p) => sum + (p.grossPnL ?? 0), 0);
    return {
      Account: a.accountNumber,
      Period: `${a.period.from} - ${a.period.to}`,
      'Realized P&L (statement)': a.reconciliation.psBlockPnLSum,
      'Realized P&L (FIFO)': round(fifoPnL),
      'P&L reconciles': flag(a.reconciliation.pnlMatches),
      'Commission': -Math.abs(a.fees.total),
      'Net P&L (realized - commission)': round(a.reconciliation.psBlockPnLSum - Math.abs(a.fees.total)),
      'Commission reconciles': flag(a.reconciliation.feesMatch),
      'Data Fees': a.cashFlow.dataFees,
      'Deposits': a.cashFlow.deposits,
      'Withdrawals': a.cashFlow.withdrawals,
      'Transfers (net)': a.cashFlow.transfersNet,
      'Net Cash Flow': a.cashFlow.net,
      'Cash reconciles': flag(a.reconciliation.cashFlowMatches),
      'Cash Balance': money(s?.cashBalance ?? null),
      'New Cash Balance': money(s?.newCashBalance ?? null),
      'Total Equity': money(s?.totalEquity ?? null),
      'Net Liq. Value': money(s?.netLiquidatingValue ?? null),
      'Trades': a.trades.length,
      'Buy Qty': a.buyQty,
      'Sell Qty': a.sellQty,
    };
  });
  const sheet = XLSX.utils.json_to_sheet(rows.length ? rows : [{ Account: '(none)' }]);
  sheet['!cols'] = [{ wch: 10 }, { wch: 20 }, ...Array(18).fill({ wch: 14 })];
  XLSX.utils.book_append_sheet(wb, sheet, 'Summary');
}

function addTradesSheet(wb: XLSX.WorkBook, r: ProcessingResult): void {
  const rows = r.accounts.flatMap(a => a.trades.map(t => ({
    Account: t.Account,
    Date: t.Date,
    Execution_Id: t.Trade_Number,
    Market: t.Market,
    Direction: t.Direction,
    Quantity: t.Quantity,
    Contract: t.Contract_Type,
    Month: t.Contract_Month,
    Expiration: t.Expiration,
    Price: t.Price_Num,
    Commission: t.Commission,
    Currency: t.Currency,
    Description: t.Original_Description,
  })));
  const sheet = XLSX.utils.json_to_sheet(rows.length ? rows : [{ Account: '(none)' }]);
  sheet['!cols'] = [{ wch: 10 }, { wch: 11 }, { wch: 12 }, { wch: 8 }, { wch: 9 }, { wch: 9 },
    { wch: 10 }, { wch: 8 }, { wch: 11 }, { wch: 11 }, { wch: 11 }, { wch: 8 }, { wch: 28 }];
  XLSX.utils.book_append_sheet(wb, sheet, 'Trades');
}

function addPositionsSheet(wb: XLSX.WorkBook, r: ProcessingResult): void {
  const rows = r.accounts.flatMap(a => a.positions.map(p => ({
    Account: p.account,
    Contract: p.contractType,
    Month: p.contractMonth,
    Direction: p.direction,
    Qty: p.quantity,
    Entry_Id: p.entryId,
    Entry_Date: p.entryDate,
    Entry_Price: p.entryPrice,
    Exit_Id: p.exitId,
    Exit_Date: p.exitDate,
    Exit_Price: p.exitPrice,
    Points: p.points,
    '$/Point': p.pointValue ?? '',
    'Gross P&L': p.grossPnL ?? '',
    Commission: p.commission,
    'Net P&L': p.netPnL ?? '',
  })));
  const sheet = XLSX.utils.json_to_sheet(rows.length ? rows : [{ Account: '(none)' }]);
  sheet['!cols'] = [{ wch: 10 }, { wch: 9 }, { wch: 8 }, { wch: 9 }, { wch: 6 }, { wch: 11 },
    { wch: 11 }, { wch: 11 }, { wch: 11 }, { wch: 11 }, { wch: 11 }, { wch: 9 }, { wch: 9 },
    { wch: 11 }, { wch: 11 }, { wch: 11 }];
  XLSX.utils.book_append_sheet(wb, sheet, 'Positions');
}

function addOpenPositionsSheet(wb: XLSX.WorkBook, r: ProcessingResult): void {
  const rows = r.accounts.flatMap(a => a.openPositions.map(p => ({
    Account: p.account,
    Contract: p.contractType,
    Expiration: p.expiration || p.contractMonth,
    Direction: p.direction,
    Qty: p.quantity,
    'Avg Price': p.avgPrice,
    '$/Point': p.pointValue ?? '',
  })));
  if (rows.length === 0) return; // omit the sheet entirely when everything is flat
  const sheet = XLSX.utils.json_to_sheet(rows);
  sheet['!cols'] = [{ wch: 10 }, { wch: 9 }, { wch: 11 }, { wch: 9 }, { wch: 6 }, { wch: 11 }, { wch: 9 }];
  XLSX.utils.book_append_sheet(wb, sheet, 'Open Positions');
}

function addEconomicsSheet(wb: XLSX.WorkBook, r: ProcessingResult): void {
  const rows = r.accounts.flatMap(a => a.economics.map(e => ({
    Account: a.accountNumber,
    Contract: e.contractType,
    'Point Value ($)': e.pointValue ?? '',
    'Tick Size': e.tickSize ?? '',
    'Tick Value ($)': e.tickValue ?? '',
    'Fee / Contract ($)': e.feePerContract ?? '',
    'Contracts (sides)': e.totalContracts,
    'Realized P&L': e.totalRealizedPnL,
    'Total Fees': e.totalFees,
    'P&S Blocks Sampled': e.sampleBlocks,
  })));
  const sheet = XLSX.utils.json_to_sheet(rows.length ? rows : [{ Account: '(none)' }]);
  sheet['!cols'] = [{ wch: 10 }, { wch: 10 }, { wch: 14 }, { wch: 10 }, { wch: 14 }, { wch: 16 },
    { wch: 16 }, { wch: 14 }, { wch: 12 }, { wch: 18 }];
  XLSX.utils.book_append_sheet(wb, sheet, 'Economics');
}

function addJournalSheet(wb: XLSX.WorkBook, r: ProcessingResult): void {
  const rows = r.accounts.flatMap(a => a.journal.map(j => ({
    Account: j.account,
    Date: j.date,
    Category: j.category,
    Description: j.description,
    Amount: j.drcr === 'CR' ? j.amount : -j.amount,
    'DR/CR': j.drcr,
  })));
  if (rows.length === 0) return;
  const sheet = XLSX.utils.json_to_sheet(rows);
  sheet['!cols'] = [{ wch: 10 }, { wch: 11 }, { wch: 12 }, { wch: 42 }, { wch: 12 }, { wch: 6 }];
  XLSX.utils.book_append_sheet(wb, sheet, 'Journal');
}

function flag(v: boolean | null): string {
  return v == null ? 'n/a' : v ? 'Yes' : 'NO';
}
